import { apiFetchWithRetry } from "./apiFetch";

/**
 * Reference / recommendation letters (staff-side). Backing endpoints under
 * /v1/admin/reference-letters, AuthGuard.SchoolAdmin; COUNSELOR+ to draft or
 * edit, any school member to read.
 */
async function json(response, fallback) {
  const data = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(data.message || fallback);
  return data;
}

export const referenceLetterService = {
  /** The student's record a letter may draw on — the same thing the model sees. */
  getRecord: async (studentId) =>
    json(await apiFetchWithRetry(`/v1/admin/reference-letters/students/${encodeURIComponent(studentId)}/record`), "Couldn't load the student's record"),

  /** Letters already written for this student, newest first. */
  listForStudent: async (studentId) =>
    json(await apiFetchWithRetry(`/v1/admin/reference-letters/students/${encodeURIComponent(studentId)}`), "Couldn't load letters"),

  /** Draft a new letter. Takes a few seconds — the model writes ~450 words. */
  draft: async (studentId, { purpose, authorNotes, featuredAchievementIds }) =>
    json(
      await apiFetchWithRetry(`/v1/admin/reference-letters/students/${encodeURIComponent(studentId)}/draft`, {
        method: "POST",
        body: JSON.stringify({ purpose, authorNotes, featuredAchievementIds }),
      }),
      "Couldn't draft the letter",
    ),

  get: async (id) => json(await apiFetchWithRetry(`/v1/admin/reference-letters/${encodeURIComponent(id)}`), "Couldn't load the letter"),

  update: async (id, body) =>
    json(
      await apiFetchWithRetry(`/v1/admin/reference-letters/${encodeURIComponent(id)}`, { method: "PUT", body: JSON.stringify({ body }) }),
      "Couldn't save the letter",
    ),

  remove: async (id) => {
    const response = await apiFetchWithRetry(`/v1/admin/reference-letters/${encodeURIComponent(id)}`, { method: "DELETE" });
    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      throw new Error(data.message || "Couldn't delete the letter");
    }
  },

  /** Fetch the PDF as a Blob (the endpoint needs the bearer token, so no plain <a href>). */
  downloadPdf: async (id) => {
    const response = await apiFetchWithRetry(`/v1/admin/reference-letters/${encodeURIComponent(id)}/pdf`);
    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      throw new Error(data.message || "Couldn't build the PDF");
    }
    const disposition = response.headers.get("Content-Disposition") || "";
    const match = disposition.match(/filename="([^"]+)"/);
    return { blob: await response.blob(), filename: match ? match[1] : "Reference letter.pdf" };
  },
};
